/**
 * Tenant Context for Follow-Up Client
 *
 * Provides tenant configuration and branding throughout the follow-up application.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { environment } from '../env/environment';
import logger from '../utils/logger';

const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

// Default branding (fallback)
const DEFAULT_BRANDING = {
  id: null,
  name: 'Empower Care',
  brandName: 'Empower Care',
  slug: 'default',
  logoUrl: null,
  faviconUrl: null,
  primaryColor: '#06AB8B',
  secondaryColor: '#049572',
  features: {}
};

// Create context
const TenantContext = createContext({
  tenant: DEFAULT_BRANDING,
  loading: true,
  error: null,
  refetch: () => {},
});

/**
 * Extract tenant identifier from current URL
 */
const extractTenantFromUrl = () => {
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);

  // 1. Check query parameter first (?tenant=slug)
  const queryTenant = searchParams.get('tenant');
  if (queryTenant) {
    localStorage.setItem('followup_tenant_slug', queryTenant);
    return { type: 'slug', value: queryTenant };
  }

  // 2. For localhost WITHOUT ?tenant= parameter - use stored or default
  if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    const storedTenant = localStorage.getItem('followup_tenant_slug');
    if (storedTenant) {
      return { type: 'slug', value: storedTenant };
    }
    return null;
  }

  // 3. For production domains — use domain-based lookup
  // The tenant DB has client_domain, followup_domain, admin_domain etc.
  // getTenantByCustomDomain checks ALL domain columns

  // 4. For known deployment domains — always use domain lookup
  // e.g., newfollowup.epainhealth.com, new-intake-followup-pyjie.sevalla.app
  const knownDomains = ['epainhealth.com', 'sevalla.app', 'kinsta.page', 'kinsta.app', 'on-aptible.com'];
  const isKnownDomain = knownDomains.some(d => hostname.endsWith(d));
  if (isKnownDomain) {
    return { type: 'domain', value: hostname };
  }

  // 5. For third-party domains: check subdomain patterns
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const subdomain = parts[0];
    if (subdomain.endsWith('-followup')) {
      return { type: 'slug', value: subdomain.replace('-followup', '') };
    }
    if (!['www', 'api', 'admin', 'app', 'followup', 'newfollowup', 'newintake', 'superadmin', 'intakeadmin'].includes(subdomain)) {
      return { type: 'slug', value: subdomain };
    }
  }

  // 6. Fallback: lookup by full domain
  return { type: 'domain', value: hostname };

  return null;
};

/**
 * Fetch tenant configuration from API
 */
const fetchTenantConfig = async (tenantInfo) => {
  try {
    let url;

    if (tenantInfo.type === 'slug') {
      url = `${environment.baseUrl}/api/tenants/slug/${tenantInfo.value}`;
    } else if (tenantInfo.type === 'domain') {
      url = `${environment.baseUrl}/api/tenants/domain/${tenantInfo.value}`;
    } else {
      return null;
    }

    const response = await fetch(url, {
      headers: {
        'X-Tenant-Domain': tenantInfo.type === 'domain' ? tenantInfo.value : '',
        'Cache-Control': 'no-cache',
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.warn('Tenant not found:', tenantInfo.value);
        return null;
      }
      throw new Error(`Failed to fetch tenant: ${response.status}`);
    }

    const data = await response.json();
    return data.tenant;
  } catch (error) {
    logger.error('Error fetching tenant config:', error);
    return null;
  }
};

/**
 * Transform API response to usable tenant object
 * Priority for colors: account_branding.followup > global primary_color > DEFAULT_BRANDING
 */
export const transformTenant = (apiTenant) => {
  if (!apiTenant) return null;

  // Get follow-up specific branding from account_branding if available
  const accountBranding = apiTenant.account_branding || {};
  const followupBranding = accountBranding.followup || {};

  // Priority: followup-specific > global > default
  const primaryColor = followupBranding.primaryColor ||
                       apiTenant.primary_color ||
                       DEFAULT_BRANDING.primaryColor;

  const secondaryColor = followupBranding.secondaryColor ||
                         apiTenant.secondary_color ||
                         DEFAULT_BRANDING.secondaryColor;

  const buttonHoverColor = followupBranding.buttonHoverColor ||
                           apiTenant.button_hover_color ||
                           null;

  logger.log('[FollowUp TenantContext] Branding colors:', {
    followupBranding,
    globalPrimary: apiTenant.primary_color,
    globalSecondary: apiTenant.secondary_color,
    usedPrimary: primaryColor,
    usedSecondary: secondaryColor
  });

  return {
    id: apiTenant.id,
    name: apiTenant.name,
    brandName: decodeHtmlEntities(apiTenant.brand_name || apiTenant.name),
    slug: apiTenant.slug,
    logoUrl: apiTenant.logo_url,
    signinLogoUrl: apiTenant.signin_logo_url,
    faviconUrl: apiTenant.favicon_url,
    primaryColor,
    secondaryColor,
    buttonHoverColor,
    customDomain: apiTenant.custom_domain,
    clientDomain: apiTenant.client_domain,
    features: apiTenant.features || {},
    settings: apiTenant.settings || {},
    accountBranding // Store full account branding for reference
  };
};

/**
 * Convert hex to RGB
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Darken a hex color by a percentage
 */
const darkenColor = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const factor = 1 - percent / 100;
  const r = Math.round(rgb.r * factor);
  const g = Math.round(rgb.g * factor);
  const b = Math.round(rgb.b * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Lighten a hex color by a percentage
 */
const lightenColor = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const factor = percent / 100;
  const r = Math.round(rgb.r + (255 - rgb.r) * factor);
  const g = Math.round(rgb.g + (255 - rgb.g) * factor);
  const b = Math.round(rgb.b + (255 - rgb.b) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Apply tenant branding to document
 */
export const applyBrandingToDocument = (tenant) => {
  const primaryColor = tenant.primaryColor || '#06AB8B';
  const secondaryColor = tenant.secondaryColor || '#049572';

  // Set primary color variants
  document.documentElement.style.setProperty('--color-primary', primaryColor);
  document.documentElement.style.setProperty('--color-primary-hover', darkenColor(primaryColor, 15));
  document.documentElement.style.setProperty('--color-primary-light', lightenColor(primaryColor, 85));
  document.documentElement.style.setProperty('--color-primary-dark', darkenColor(primaryColor, 30));

  // Set secondary color variants
  document.documentElement.style.setProperty('--color-secondary', secondaryColor);
  document.documentElement.style.setProperty('--color-secondary-hover', darkenColor(secondaryColor, 15));
  document.documentElement.style.setProperty('--color-secondary-light', lightenColor(secondaryColor, 85));
  document.documentElement.style.setProperty('--color-secondary-dark', darkenColor(secondaryColor, 30));

  // Button styling
  const hoverColor = tenant.buttonHoverColor || darkenColor(primaryColor, 15);
  document.documentElement.style.setProperty('--color-primary-hover', hoverColor);
  document.documentElement.style.setProperty('--color-button-hover', hoverColor);

  // Status colors based on primary
  document.documentElement.style.setProperty('--color-success', secondaryColor);
  document.documentElement.style.setProperty('--color-success-light', lightenColor(secondaryColor, 85));
  document.documentElement.style.setProperty('--color-info', primaryColor);
  document.documentElement.style.setProperty('--color-info-light', lightenColor(primaryColor, 85));

  // Focus shadow
  const rgb = hexToRgb(primaryColor);
  if (rgb) {
    document.documentElement.style.setProperty('--shadow-focus', `0 0 0 4px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`);
  }

  // Set tenant ID on window for API access
  if (tenant.id) {
    // Clear any old tenant data first to prevent cross-tenant contamination
    const oldTenantId = localStorage.getItem('followup_tenant_id');
    if (oldTenantId && oldTenantId !== String(tenant.id)) {
      logger.log('[FollowUp TenantContext] ⚠️ Tenant changed from', oldTenantId, 'to', tenant.id, '- clearing old session data');
      // Clear old session data when switching tenants
      sessionStorage.removeItem('followup_form_state');
    }

    window.__TENANT_ID__ = tenant.id;
    localStorage.setItem('followup_tenant_id', String(tenant.id));
    localStorage.setItem('followup_tenant_slug', tenant.slug);
    logger.log('[FollowUp TenantContext] ✅ Set tenant ID:', tenant.id, 'slug:', tenant.slug);
  }

  // Remove ALL existing favicon links first
  const existingIcons = document.querySelectorAll("link[rel*='icon']");
  existingIcons.forEach(icon => icon.remove());

  // Set favicon — skip cache-busting for base64 data URLs
  if (tenant.faviconUrl) {
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = tenant.faviconUrl.startsWith('data:')
      ? tenant.faviconUrl.split(';')[0].split(':')[1]
      : 'image/x-icon';
    faviconLink.href = tenant.faviconUrl.startsWith('data:')
      ? tenant.faviconUrl
      : tenant.faviconUrl + '?_t=' + Date.now();
    document.head.appendChild(faviconLink);
  }

  // Set page title
  if (tenant.brandName) {
    document.title = `${tenant.brandName} - Follow-Up Form`;
  }
};

/**
 * Tenant Provider Component
 */
export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTenant = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if tenant was pre-fetched by the early detection script in index.html
      if (window.__TENANT_DATA__) {
        logger.log('[FollowUp TenantContext] Using pre-fetched tenant data:', window.__TENANT_DATA__.id);
        const transformedTenant = transformTenant(window.__TENANT_DATA__);

        if (transformedTenant) {
          setTenant(transformedTenant);
          applyBrandingToDocument(transformedTenant);
        }

        delete window.__TENANT_DATA__;
        return;
      }

      const tenantInfo = extractTenantFromUrl();

      if (!tenantInfo) {
        setTenant(DEFAULT_BRANDING);
        applyBrandingToDocument(DEFAULT_BRANDING);
        return;
      }

      const apiTenant = await fetchTenantConfig(tenantInfo);

      if (apiTenant) {
        const transformedTenant = transformTenant(apiTenant);
        setTenant(transformedTenant);
        applyBrandingToDocument(transformedTenant);
      } else {
        setTenant(DEFAULT_BRANDING);
        applyBrandingToDocument(DEFAULT_BRANDING);
      }
    } catch (err) {
      logger.error('Error loading tenant:', err);
      setError(err.message);
      setTenant(DEFAULT_BRANDING);
      applyBrandingToDocument(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenant();
  }, [loadTenant]);

  const value = {
    tenant,
    loading,
    error,
    refetch: loadTenant,
    getLogo: () => tenant.logoUrl,
    getPrimaryColor: () => tenant.primaryColor,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to use tenant context
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export default TenantContext;
